<template>
  <div class="visualization-content pa-4">
    <v-card outlined elevation="2" class="fact-chart-panel">
      <v-card-title class="visualization-title">
        <v-icon left color="primary">mdi-chart-bar</v-icon>
        Fact Chart
        <v-spacer></v-spacer>
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          size="22"
          width="2"
          color="primary"
        ></v-progress-circular>
      </v-card-title>
      <v-card-text>
        <div class="fact-chart-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Chart type</div>
            <v-btn-toggle v-model="factPieMetric" mandatory dense color="primary" class="fact-chart-toggle">
              <v-btn v-for="item in factPieMetricOptions" :key="item.value" :value="item.value" small>
                {{ item.text }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Breakdown</div>
            <v-btn-toggle v-model="factPieSubOption" mandatory dense color="primary" class="fact-chart-toggle">
              <v-btn v-for="option in factPieSubOptions" :key="option.value" :value="option.value" small>
                {{ option.text }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <v-alert v-if="error" type="warning" outlined dense class="mb-4">
          {{ error }}
        </v-alert>

        <div class="fact-pie-layout">
          <div class="fact-pie-canvas-wrap">
            <canvas ref="factPieChart"></canvas>
          </div>
          <div class="fact-pie-meta">
            <div class="fact-pie-kicker">{{ factChartData ? factChartData.country_name : selectedCountryName }}</div>
            <div class="chart-title-line">
              <h3>{{ activeFactPieSeries.title || 'Pie Chart' }}</h3>
              <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeFactPieSeries.source_range" />
            </div>
            <div class="fact-pie-unit">{{ activeFactPieSeries.unit }}</div>
            <div class="fact-pie-list">
              <div v-for="item in activeFactPieSeries.items" :key="item.label" class="fact-pie-list-row">
                <span>{{ item.label }}</span>
                <strong>{{ formatFactPieValue(item.value, activeFactPieSeries.unit) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <v-divider class="my-6"></v-divider>

        <div class="fact-stock-section">
          <div class="fact-stock-header">
            <div>
              <div class="chart-title-line">
                <h3>{{ formatChartTitle(activeFactStockSeries, 'Vehicle Stock') }}</h3>
                <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeFactStockSeries.source_range" />
              </div>
            </div>
            <v-btn-toggle v-model="factStockOption" mandatory dense color="primary" class="fact-stock-toggle">
                <v-btn value="bau_core" small>BAU 6 types</v-btn>
                <v-btn value="bau_all" small>BAU 8 types</v-btn>
                <v-btn value="scenario_core" small>30x30 6 types</v-btn>
                <v-btn value="scenario_all" small>30x30 8 types</v-btn>
            </v-btn-toggle>
          </div>
          <div class="fact-stock-canvas-wrap">
            <canvas ref="factStockChart"></canvas>
          </div>
        </div>

        <v-divider class="my-6"></v-divider>

        <div class="fact-stock-section">
          <div class="fact-stock-header">
            <div>
              <div class="chart-title-line">
                <h3>{{ formatChartTitle(activeFactDemandSeries, 'Transport Demand') }}</h3>
                <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeFactDemandSeries.source_range" />
              </div>
            </div>
            <v-btn-toggle v-model="factDemandOption" mandatory dense color="primary" class="fact-stock-toggle">
                <v-btn value="core" small>6 types</v-btn>
              <v-btn value="all" small>All fleet</v-btn>
            </v-btn-toggle>
          </div>
          <div class="fact-stock-canvas-wrap">
            <canvas ref="factDemandChart"></canvas>
          </div>
        </div>

        <v-divider class="my-6"></v-divider>

        <div class="fact-stock-section">
          <div class="fact-stock-header">
            <div>
              <h3>Vehicle Price Trends</h3>
            </div>
          </div>
          <div class="fact-price-layout">
            <div class="fact-price-panel">
              <div class="fact-price-panel-header">
                <div>
                  <div class="chart-title-line">
                    <h4>{{ formatChartTitle(activeFactPriceSelectedSeries, 'Selected Vehicles') }}</h4>
                    <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeFactPriceSelectedSeries.source_range" />
                    <span v-if="activeFactPriceSelectedSeries.subtitle" class="chart-note-chip">
                      {{ activeFactPriceSelectedSeries.subtitle }}
                    </span>
                  </div>
                </div>
                <v-btn-toggle v-model="factPriceLeftOption" mandatory dense color="primary" class="fact-price-left-toggle">
                  <v-btn v-for="option in factPriceLeftOptions" :key="option.value" :value="option.value" small>
                    {{ option.text }}
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div class="fact-price-canvas-wrap">
                <canvas ref="factPriceSelectedChart"></canvas>
              </div>
            </div>
            <div class="fact-price-panel">
              <div class="fact-price-panel-header">
                <div>
                  <div class="chart-title-line">
                    <h4>{{ formatChartTitle(activeFactPriceAllSeries, 'All Vehicles') }}</h4>
                    <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeFactPriceAllSeries.source_range" />
                    <span v-if="activeFactPriceAllSeries.subtitle" class="chart-note-chip">
                      {{ activeFactPriceAllSeries.subtitle }}
                    </span>
                  </div>
                </div>
                <v-btn-toggle v-model="factPriceOption" mandatory dense color="primary" class="fact-price-left-toggle">
                  <v-btn value="domestic_new" small>Domestic new</v-btn>
                  <v-btn value="imported_new" small>Imported new</v-btn>
                  <v-btn value="imported_second_hand" small>Imported second-hand</v-btn>
                </v-btn-toggle>
              </div>
              <div class="fact-price-canvas-wrap">
                <canvas ref="factPriceAllChart"></canvas>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ChartJS from './chartSetup'
import {
  DEFAULT_CHART_COLOR,
  PIE_CHART_COLORS,
  getPowertrainColor,
  getVehiclePriceColor,
  getVehicleTypeColor
} from './colorPalette'
import SourceTooltipIcon from './SourceTooltipIcon.vue'
import { SHOW_PARAMETER_SOURCE_TOOLTIPS } from './sourceTooltipConfig'

export default {
  name: 'FactChartTab',
  components: {
    SourceTooltipIcon
  },
  props: {
    domain: {
      type: String,
      required: true
    },
    analysisResult: {
      type: Object,
      default: null
    },
    selectedCountryName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      factChartData: null,
      error: '',
      isLoading: false,
      factPieMetric: 'power_generation',
      factPieSubOption: 'traditional',
      factPieMetricOptions: [
        { text: 'Power mix', value: 'power_generation' },
        { text: 'Fleet size', value: 'fleet_size' }
      ],
      factPieChartInstance: null,
      factStockOption: 'bau_core',
      factStockChartInstance: null,
      factDemandOption: 'core',
      factDemandChartInstance: null,
      factPriceLeftOption: 'light',
      factPriceLeftOptions: [
        { text: 'Light vehicles', value: 'light' },
        { text: 'Heavy vehicles', value: 'heavy' },
        { text: '2W / 3W', value: 'two_three' }
      ],
      factPriceOption: 'domestic_new',
      factPriceSelectedChartInstance: null,
      factPriceAllChartInstance: null
    }
  },
  computed: {
    showParameterSourceTooltips() {
      return SHOW_PARAMETER_SOURCE_TOOLTIPS
    },
    factPieSubOptions() {
      if (this.factPieMetric === 'fleet_size') {
        return [
          { text: '6 types', value: 'core' },
          { text: 'All', value: 'all' }
        ]
      }
      return [
        { text: 'Traditional energy', value: 'traditional' },
        { text: 'Clean energy', value: 'clean' }
      ]
    },
    activeFactPieSeries() {
      const emptySeries = { title: '', unit: '', items: [] }
      if (!this.factChartData || !this.factChartData.pie) return emptySeries

      const metricData = this.factChartData.pie[this.factPieMetric]
      if (!metricData) return emptySeries

      return metricData[this.factPieSubOption] || emptySeries
    },
    activeFactStockSeries() {
      const emptySeries = { title: '', unit: '', years: [], datasets: [] }
      if (!this.factChartData || !this.factChartData.vehicle_stock) return emptySeries
      return this.factChartData.vehicle_stock[this.factStockOption] || emptySeries
    },
    activeFactDemandSeries() {
      const emptySeries = { title: '', unit: '', years: [], datasets: [] }
      if (!this.factChartData || !this.factChartData.transport_demand) return emptySeries
      return this.factChartData.transport_demand[this.factDemandOption] || emptySeries
    },
    activeFactPriceSeries() {
      const emptySeries = { title: '', subtitle: '', unit: '', years: [], datasets: [] }
      if (!this.factChartData || !this.factChartData.price_trends) return emptySeries
      return this.factChartData.price_trends[this.factPriceOption] || emptySeries
    },
    activeFactPriceSelectedSeries() {
      const baseSeries = this.factChartData && this.factChartData.price_trends
        ? this.factChartData.price_trends.domestic_new
        : null
      const emptySeries = { title: '', subtitle: '', unit: '', years: [], datasets: [] }
      if (!baseSeries) return emptySeries

      const groups = {
        light: {
          title: 'Projected Price of a Domestic New Vehicle',
          subtitle: 'sales taxes and subsidies included',
          labels: ['Petrol Car', 'BEV Car', 'Diesel LCV', 'BEV LCV']
        },
        heavy: {
          title: 'Projected Price of a Domestic New Vehicle',
          subtitle: 'sales taxes and subsidies included',
          labels: ['Diesel Bus', 'BEV Bus', 'Diesel HDV', 'BEV HDV']
        },
        two_three: {
          title: 'Projected Price of a Domestic New Vehicle',
          subtitle: 'sales taxes and subsidies included',
          labels: ['Petrol 2W', 'BEV 2W', 'Petrol 3W', 'BEV 3W']
        }
      }
      const selectedGroup = groups[this.factPriceLeftOption] || groups.light
      const selectedLabels = new Set(selectedGroup.labels)

      return {
        ...baseSeries,
        title: selectedGroup.title,
        subtitle: selectedGroup.subtitle,
        datasets: (baseSeries.datasets || []).filter(dataset => selectedLabels.has(dataset.label))
      }
    },
    activeFactPriceAllSeries() {
      return this.activeFactPriceSeries
    }
  },
  watch: {
    analysisResult() {
      this.fetchFactChartData()
    },
    factPieMetric(newMetric) {
      this.factPieSubOption = newMetric === 'fleet_size' ? 'core' : 'traditional'
    },
    factPieSubOption() {
      this.$nextTick(this.renderFactPieChart)
    },
    factStockOption() {
      this.$nextTick(this.renderFactStockChart)
    },
    factDemandOption() {
      this.$nextTick(this.renderFactDemandChart)
    },
    factPriceOption() {
      this.$nextTick(this.renderFactPriceAllChart)
    },
    factPriceLeftOption() {
      this.$nextTick(this.renderFactPriceSelectedChart)
    }
  },
  mounted() {
    this.fetchFactChartData()
  },
  beforeDestroy() {
    this.destroyFactPieChart()
    this.destroyFactStockChart()
    this.destroyFactDemandChart()
    this.destroyFactPriceChart()
  },
    methods: {
      getPreloadedFactChartData() {
        const data = this.analysisResult && this.analysisResult.preloaded_outputs
          ? this.analysisResult.preloaded_outputs.fact_chart || null
          : null
        return this.hasUsablePreloadedFactChartData(data) ? data : null
      },
      hasUsablePreloadedFactChartData(data) {
        if (!data || !data.vehicle_stock || !data.transport_demand) return false
        const hasStock = Object.values(data.vehicle_stock).some((series) => series && Array.isArray(series.datasets) && series.datasets.length)
        const hasDemand = Object.values(data.transport_demand).some((series) => series && Array.isArray(series.datasets) && series.datasets.length)
        return hasStock || hasDemand
      },
    async fetchFactChartData() {
      const requestedFileName = this.analysisResult ? this.analysisResult.file_name : null
      const preloadedData = this.getPreloadedFactChartData()
      try {
        this.isLoading = true
        this.error = ''
        if (preloadedData) {
          console.log('[Output Read] Fact Chart:', preloadedData.file_name || requestedFileName || 'preloaded data')
          this.$emit('file-read', {
            section: 'fact_chart',
            sectionTitle: 'Fact Chart',
            fileName: preloadedData.file_name || requestedFileName
          })
          this.factChartData = preloadedData
          this.$nextTick(() => {
            this.renderFactPieChart()
            this.renderFactStockChart()
            this.renderFactDemandChart()
            this.renderFactPriceChart()
          })
          return
        }

        this.$emit('loading-state', {
          section: 'fact_chart',
          sectionTitle: 'Fact Chart',
          fileName: requestedFileName,
          isLoading: true
        })

        const response = await fetch(this.domain + '/zev/fact_chart_data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_name: requestedFileName
          })
        })

        const data = await response.json()
        if (!response.ok) {
          throw new Error(data.detail || data.error || `HTTP error! status: ${response.status}`)
        }
        if (data.error) throw new Error(data.error)

        this.factChartData = data
        console.log('[Output Read] Fact Chart:', data.file_name || requestedFileName || 'latest analysis workbook')
        this.$emit('file-read', {
          section: 'fact_chart',
          sectionTitle: 'Fact Chart',
          fileName: data.file_name || requestedFileName
        })
        this.$nextTick(() => {
          this.renderFactPieChart()
          this.renderFactStockChart()
          this.renderFactDemandChart()
          this.renderFactPriceChart()
        })
      } catch (error) {
        console.error('Error loading Fact Chart data:', error)
        this.error = error.message
        this.factChartData = null
        this.destroyFactPieChart()
        this.destroyFactStockChart()
        this.destroyFactDemandChart()
        this.destroyFactPriceChart()
      } finally {
        this.isLoading = false
        this.$emit('loading-state', {
          section: 'fact_chart',
          sectionTitle: 'Fact Chart',
          fileName: requestedFileName,
          isLoading: false
        })
      }
    },
    destroyChartOnCanvas(canvas) {
      if (!canvas || !ChartJS.getChart) return
      const existingChart = ChartJS.getChart(canvas)
      if (existingChart) existingChart.destroy()
    },
    destroyFactPieChart() {
      if (this.factPieChartInstance) {
        this.factPieChartInstance.destroy()
        this.factPieChartInstance = null
      }
      this.destroyChartOnCanvas(this.$refs.factPieChart)
    },
    destroyFactStockChart() {
      if (this.factStockChartInstance) {
        this.factStockChartInstance.destroy()
        this.factStockChartInstance = null
      }
      this.destroyChartOnCanvas(this.$refs.factStockChart)
    },
    destroyFactDemandChart() {
      if (this.factDemandChartInstance) {
        this.factDemandChartInstance.destroy()
        this.factDemandChartInstance = null
      }
      this.destroyChartOnCanvas(this.$refs.factDemandChart)
    },
    destroyFactPriceChart() {
      if (this.factPriceSelectedChartInstance) {
        this.factPriceSelectedChartInstance.destroy()
        this.factPriceSelectedChartInstance = null
      }
      if (this.factPriceAllChartInstance) {
        this.factPriceAllChartInstance.destroy()
        this.factPriceAllChartInstance = null
      }
      this.destroyChartOnCanvas(this.$refs.factPriceSelectedChart)
      this.destroyChartOnCanvas(this.$refs.factPriceAllChart)
    },
    renderFactPieChart() {
      const canvas = this.$refs.factPieChart
      if (!canvas) return

      const series = this.activeFactPieSeries
      const items = series.items || []
      this.destroyFactPieChart()

      if (!items.length) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        return
      }

      this.factPieChartInstance = new ChartJS(canvas.getContext('2d'), {
        type: 'pie',
        data: {
          labels: items.map(item => item.label),
          datasets: [{
            data: items.map(item => item.value),
            backgroundColor: items.map((item, index) => {
              return getPowertrainColor(item.label) !== DEFAULT_CHART_COLOR
                ? getPowertrainColor(item.label)
                : getVehicleTypeColor(item.label) !== DEFAULT_CHART_COLOR
                  ? getVehicleTypeColor(item.label)
                  : PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]
            }),
            borderColor: '#ffffff',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (context) => `${context.label}: ${this.formatFactPieValue(context.parsed, series.unit)}`
              }
            }
          }
        }
      })
    },
    renderFactStockChart() {
      this.factStockChartInstance = this.renderFactStackedAreaChart(
        this.$refs.factStockChart,
        this.factStockChartInstance,
        this.activeFactStockSeries,
        getPowertrainColor
      )
    },
    renderFactDemandChart() {
      this.factDemandChartInstance = this.renderFactStackedAreaChart(
        this.$refs.factDemandChart,
        this.factDemandChartInstance,
        this.activeFactDemandSeries,
        getVehicleTypeColor
      )
    },
    renderFactPriceChart() {
      this.renderFactPriceSelectedChart()
      this.renderFactPriceAllChart()
    },
    formatChartTitle(series, fallback) {
      const title = (series && series.title) || fallback
      const axisTitle = series && (series.y_axis_title || series.unit)
      if (!axisTitle || title.toLowerCase().includes(String(axisTitle).toLowerCase())) {
        return title
      }
      return `${title} (${axisTitle})`
    },
    renderFactPriceSelectedChart() {
      this.factPriceSelectedChartInstance = this.renderFactLineChart(
        this.$refs.factPriceSelectedChart,
        this.factPriceSelectedChartInstance,
        this.activeFactPriceSelectedSeries,
        { legendPosition: 'bottom' }
      )
    },
    renderFactPriceAllChart() {
      this.factPriceAllChartInstance = this.renderFactLineChart(
        this.$refs.factPriceAllChart,
        this.factPriceAllChartInstance,
        this.activeFactPriceAllSeries,
        { legendPosition: 'right' }
      )
    },
    renderFactLineChart(canvas, chartInstance, series, options = {}) {
      if (!canvas) return chartInstance
      if (chartInstance) chartInstance.destroy()
      this.destroyChartOnCanvas(canvas)

      const datasets = series.datasets || []
      if (!datasets.length) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        return null
      }

      return new ChartJS(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: series.years,
          datasets: datasets.map(dataset => ({
            label: dataset.label,
            data: dataset.values,
            borderColor: getVehiclePriceColor(dataset.label),
            backgroundColor: getVehiclePriceColor(dataset.label),
            borderWidth: 2,
            borderDash: this.isZevSeries(dataset.label) ? [8, 5] : [],
            pointStyle: 'line',
            pointRadius: 0,
            fill: false,
            tension: 0.25
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: options.legendPosition || 'bottom',
              labels: {
                usePointStyle: true,
                pointStyle: 'line',
                pointStyleWidth: 34,
                padding: 12,
                generateLabels: (chart) => {
                  const labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart)
                  return labels.map(label => {
                    const dataset = chart.data.datasets[label.datasetIndex] || {}
                    return {
                      ...label,
                      pointStyle: 'line',
                      fillStyle: dataset.borderColor,
                      strokeStyle: dataset.borderColor,
                      lineDash: dataset.borderDash || [],
                      lineWidth: dataset.borderWidth || 2
                    }
                  })
                }
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${Math.round(context.parsed.y).toLocaleString()}`
              }
            }
          },
          scales: {
            x: { grid: { display: false } },
            y: {
              beginAtZero: true,
              ticks: { callback: (value) => Number(value).toLocaleString() }
            }
          }
        }
      })
    },
    renderFactStackedAreaChart(canvas, chartInstance, series, getColor) {
      if (!canvas) return chartInstance
      if (chartInstance) chartInstance.destroy()
      this.destroyChartOnCanvas(canvas)

      const datasets = series.datasets || []
      if (!datasets.length) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        return null
      }

      return new ChartJS(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: series.years,
          datasets: datasets.map(dataset => ({
            label: dataset.label,
            data: dataset.values,
            backgroundColor: getColor(dataset.label),
            borderColor: getColor(dataset.label),
            borderWidth: 1,
            pointRadius: 0,
            fill: true,
            tension: 0.25
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${Math.round(context.parsed.y).toLocaleString()}`
              }
            }
          },
          scales: {
            x: { stacked: true, grid: { display: false } },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { callback: (value) => Number(value).toLocaleString() }
            }
          }
        }
      })
    },
    isZevSeries(label) {
      const normalized = String(label || '').toUpperCase()
      return normalized.includes('BEV') || normalized.includes('FCEV')
    },
    formatFactPieValue(value, unit) {
      if (unit && unit.includes('%')) {
        return `${Number(value).toFixed(1)}%`
      }
      return Math.round(Number(value)).toLocaleString()
    }
  }
}
</script>
